import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { MacroCollaborator } from '@/types/settings-api';

/**
 * Canonical query-key factory. Same pattern as other settings hooks.
 * Keyed on (ownerId, providerId, search) so each unique combination
 * gets its own cache slot.
 */
export function macroCollaboratorsSearchQueryKey(
  ownerId: string | null | undefined,
  providerId: string | null | undefined,
  search: string,
): readonly [
  string,
  string,
  string | null | undefined,
  string | null | undefined,
  string,
] {
  return [
    'settings',
    'macro-collaborators-search',
    ownerId,
    providerId,
    search,
  ];
}

/**
 * Wire-side row from `GET /api/Doctor/GetProviderAndNurseList`.
 *
 * **DIFFERENT shape from `MacroCollaborator`** even though Flutter
 * reuses the same model in-memory. The endpoint returns the user-
 * directory shape with `{id, name, role, photo}` — `id` here is the
 * user's id (not a `macro_collaborators` row id, since the row doesn't
 * exist yet), and there's no `macroId`. Flutter handles this with a
 * separate `MacroCollaboratorList` DTO + `toMacroCollaborator()`
 * adapter (see `data/datamodels/macro_collaborator_list.dart:30-49`).
 *
 * Verified against live dev cURL 2026-05-19:
 *   ```json
 *   [{
 *     "id": "035819e5-3ace-ea11-91a7-0cc47a65f3a2",
 *     "name": "Nader Hebela",
 *     "role": "Provider",
 *     "photo": "https://...blob.core.windows.net/...?sv=..."
 *   }]
 *   ```
 */
interface ProviderAndNurseListItem {
  id: string;
  name: string;
  role: string;
  photo: string | null;
}

/**
 * Macro collaborator search — PROD-764 PR-C.
 *
 * `GET /api/Doctor/GetProviderAndNurseList?ownerId={}&providerId={}&search={}`
 *
 * Wire returns a bare array of `ProviderAndNurseListItem` (the user-
 * directory shape — see the type's JSDoc). The hook adapts each row to
 * the canonical `MacroCollaborator` shape so the rest of the React side
 * (picker, save body, selection identity) consumes one consistent
 * model. Adapter mirrors Flutter `macro_collaborator_list.dart:45-49`:
 *
 *   ```dart
 *   MacroCollaborator toMacroCollaborator() => MacroCollaborator(
 *     collaboratorUserId: id,          // user id from search → user id key
 *     collaboratorPhotoUrl: photo,
 *     collaboratorName: name,
 *   );
 *   // id (row-id) is left unset — server allocates on save.
 *   // role is dropped — not displayed in the picker.
 *   ```
 *
 * **Wire-shape verified** against captured dev cURLs:
 *   - 2026-05-18: Empty search + ownerId=providerId → `[]` (the
 *     practitioner can't collaborate with themselves; demo org just
 *     has no other practitioners eligible).
 *   - 2026-05-19: Populated response confirms the `{id, name, role,
 *     photo}` shape (the original assumption that the wire matched
 *     MacroCollaborator was wrong; fixed in the same commit as this
 *     comment).
 *   - Missing `providerId` → 400 with .NET validation envelope.
 *     Both ownerId and providerId are required GUIDs.
 *
 * **Param semantics**:
 *   - `ownerId`: the macro's `ownerId` when editing an existing macro;
 *     else the current practitioner's id (Flutter `macro_tab.dart:1646`).
 *   - `providerId`: always the current practitioner being viewed.
 *   - `search`: free-text search term — server filters by name.
 *     Empty string returns the full eligible set.
 *
 * Flutter source: `settings_data_source_impl.dart:1502-1518`
 * (`getMacroCollaborators`).
 */
export function useMacroCollaboratorsSearch(
  ownerId: string | null | undefined,
  providerId: string | null | undefined,
  search: string = '',
) {
  return useQuery({
    queryKey: macroCollaboratorsSearchQueryKey(ownerId, providerId, search),
    queryFn: async ({ signal }) => {
      if (!ownerId || !providerId) {
        throw new Error(
          'useMacroCollaboratorsSearch called without ownerId or providerId',
        );
      }
      // The server requires ownerId AND providerId as non-empty GUIDs.
      // Empty `search` is fine — returns the full eligible set.
      const qs = new URLSearchParams({
        ownerId,
        providerId,
        search,
      });
      const rows = await get<ProviderAndNurseListItem[]>(
        `/api/Doctor/GetProviderAndNurseList?${qs.toString()}`,
        { signal },
      );
      // Adapt wire → canonical model. Mirrors Flutter's
      // MacroCollaboratorList.toMacroCollaborator().
      return rows.map(
        (r): MacroCollaborator => ({
          id: '',
          collaboratorUserId: r.id ?? '',
          macroId: '',
          collaboratorName: r.name ?? '',
          collaboratorPhotoUrl: r.photo ?? null,
        }),
      );
    },
    enabled: !!ownerId && !!providerId,
  });
}
